import { C1_CONTRACT, C1_PROVISIONAL_STATUS_ID, c1ProvisionalStatusChainId, type PossessorCompileContext } from './possessor-compile.js';
import { applyStatusDamageDefenseDelta, deriveDamageSourceKind, STATUS_DAMAGE_DEFENSE_LAYERS } from './creature-defense-delta.js';
import { statusDefinitionLayerNames, statusProjectionDependencies } from './status-attack-budget.js';
import { createStatusLayerReadContext, leanStatusEntry } from './status-row-construction.js';
import { createComputeSourceCleaner } from './compute-source-cleanup.js';
import { storedStatusRows } from './creature-status-reader.js';
import { readItemField, type ItemFieldRow } from './item-field-reader.js';
import { readLayerListEntryUuid } from './inventory-raw-readers.js';
import { encounterCreatureField } from '../../shared/array-types.js';
import { statusRowOnlyLayer } from '../statuses/runtime-layers.js';
import { compileStatusResidueFields } from './status-root-residue.js';
import { layerReadView } from './layer-read-view.js';
import { statusHasCompileIdDependency } from './member-status-diff.js';
import type { MemberRecompileResult, MemberStatusStep } from './member-status-recompile.js';

type Row = Record<string, unknown>;
const record = (value: unknown): Row | null => value !== null && typeof value === 'object' && !Array.isArray(value) ? value as Row : null;
const upper = (value: unknown): string => String(value ?? '').trim().toUpperCase();
const damageLayer = (key: string): boolean => STATUS_DAMAGE_DEFENSE_LAYERS.some(layer => layer === key);

/** The status/root share of the existing defense fold, with no equipment, speed,
 * skill or vitality pass. null retains the scene/dependency-aware native path. */
export function recompileMemberStatusDamageDefenses(row: Row, steps: MemberStatusStep[],
  ctx: Pick<PossessorCompileContext, 'definition' | 'authoredDefinition' | 'catalog'>): MemberRecompileResult | null {
  const carrier = encounterCreatureField(row.encounter_creatures, 'item_type');
  const computed = record(row.computed), creature = record(computed?.creature), vitality = record(computed?.vitality);
  if (!steps.length || !creature || !vitality || carrier !== 'player_character' && carrier !== 'creature') return null;
  const definitions = steps.map(step => ctx.definition(upper(step.statusItemId)));
  const shielded = C1_CONTRACT.vitality.statusCatalog.rows.find(status => status.variable_name === 'shielded')?.item_id;
  if (!definitions.every(def => def && readItemField(def, 'item_type') !== 'creature'
    && (!statusHasCompileIdDependency(def) || upper(readItemField(def, 'item_id')) === upper(shielded))
    && statusDefinitionLayerNames(def).some(damageLayer)
    && statusDefinitionLayerNames(def).every(key => damageLayer(key) || statusRowOnlyLayer(key))
    && !statusDefinitionLayerNames(def).includes('buff_does_not_layer_onto_opposing_team'))) return null;

  try {
    const deps = statusProjectionDependencies(row, ctx.definition), readers = createStatusLayerReadContext(deps);
    const changed = new Set(steps.map(step => upper(step.statusItemId)));
    // A retained ability can read the changed status as a condition. Keep its
    // existing dependency path, even though the changed definition is defense-only.
    for (const catalog of [null, record(row.catalog), ctx.catalog]) {
      for (const def of catalog ? Object.values(catalog) : [row]) {
        if (!record(def)) continue;
        for (const key of ['if_caster_has_status_effect_active', 'if_caster_has_status_effect_not_active', 'if_aura_owner_has_status_effect_active']) {
          if (!statusDefinitionLayerNames(def as Row).includes(key)) continue;
          if (Object.values(readers.layerEntries(def as Row, key)).some(entry => changed.has(upper(readLayerListEntryUuid(entry))))) return null;
        }
      }
    }
    const targetId = upper(encounterCreatureField(row.encounter_creatures, 'encounter_creature_id'));
    const hostId = String(readItemField(row, 'item_id', deps) ?? encounterCreatureField(row.encounter_creatures, 'item_id') ?? '');
    const initialDefenses = record(creature.defenses) ?? undefined;
    const priorSource = Object.values(initialDefenses ?? {}).flatMap(value => Array.isArray(value) ? value : [])
      .find(value => record(value) && upper(value.source_item_id) === upper(hostId) && typeof value.source_name === 'string');
    const cleaner = createComputeSourceCleaner(deps);
    const host = { id: hostId, name: record(priorSource) ? String(priorSource.source_name) : cleaner.name(hostId, row) ?? '',
      kind: record(priorSource) && typeof priorSource.source_kind === 'string' ? priorSource.source_kind : deriveDamageSourceKind(cleaner.type(hostId, row)) };
    if (!host.id || !host.name || !host.kind) return null;
    // Finalized NPC roots contain flattened status residue. Only the authored
    // creature definition can distinguish it from the creature's own defenses.
    const own = carrier === 'creature' ? (ctx.authoredDefinition ?? ctx.definition)(upper(encounterCreatureField(row.encounter_creatures, 'item_id'))) : null;
    if (carrier === 'creature' && (!own || !record(own.item_layer_types) || record(own.encounter_creatures))) return null;
    const entries = (source: ItemFieldRow, key: string): unknown[] => Object.values(readers.layerEntries(source, key));
    const types = (source: Row, key: string): string[] => entries(source, key)
      .map(entry => upper(record(entry)?.damage_type ?? readLayerListEntryUuid(entry))).filter(Boolean);
    const hostOwn = Object.fromEntries(STATUS_DAMAGE_DEFENSE_LAYERS.map(layer => [layer, own ? types(own, layer) : []]));
    let target = storedStatusRows(row, 'target'), caster = storedStatusRows(row, 'caster'), defenses = initialDefenses;
    const addIds: string[] = [];
    for (const [index, step] of steps.entries()) {
      const def = definitions[index]!;
      let id: string;
      if (step.kind === 'remove') {
        id = upper(step.statusId);
        const held = target.find(status => upper(readItemField(status, 'status_id')) === id);
        if (!held || upper(readItemField(held, 'item_id', deps)) !== upper(step.statusItemId)) return null;
        target = target.filter(status => upper(readItemField(status, 'status_id')) !== id);
        caster = caster.filter(status => upper(readItemField(status, 'status_id')) !== id);
      } else {
        id = upper(step.statusId) || (steps.length === 1 ? C1_PROVISIONAL_STATUS_ID : c1ProvisionalStatusChainId(addIds.length));
        const entry = leanStatusEntry({ status_id: id, status_target_id: targetId, status_item_id: upper(step.statusItemId),
          status_stack: step.stack ?? 1, ...(upper(step.casterId) === targetId ? {} : { status_caster_id: upper(step.casterId) }) }, true) as Row;
        target = [entry, ...target];
        if (upper(step.casterId) === targetId) caster = [entry, ...caster];
        addIds.push(id);
      }
      const otherHeld = Object.fromEntries(STATUS_DAMAGE_DEFENSE_LAYERS.map(layer => [layer, target
        .filter(status => upper(readItemField(status, 'status_id')) !== id)
        .flatMap(status => {
          const statusDef = record(status.status_arr) ?? ctx.definition(upper(readItemField(status, 'item_id', deps)));
          if (!statusDef) throw new Error('def-missing');
          return types(statusDef, layer);
        })]));
      const delta = applyStatusDamageDefenseDelta({ defenses, host, applied: step.kind === 'add',
        fragments: Object.fromEntries(STATUS_DAMAGE_DEFENSE_LAYERS.map(layer => [layer, entries(def, layer)])), hostOwn, otherHeld });
      if (!delta.ok) return null;
      defenses = delta.defenses;
    }
    const after = layerReadView(row, { statuses: { ...record(row.statuses), target, caster } });
    const rootFields = compileStatusResidueFields(row, after, { definition: ctx.definition, entries,
      value: readers.layerValue, quantityIds: C1_CONTRACT.rollup.quantityIds }, STATUS_DAMAGE_DEFENSE_LAYERS);
    const next = { ...creature };
    if (defenses) next.defenses = defenses; else delete next.defenses;
    const catalog = record(row.catalog) ?? ctx.catalog ?? null, additions: Row = {};
    for (const [index, step] of steps.entries()) if (step.kind === 'add' && !catalog?.[upper(step.statusItemId)])
      additions[upper(step.statusItemId)] = definitions[index]!;
    // Borrow existing definitions and keys. Only new membership belongs to this
    // read view; serializing it produces the ordinary catalog wire shape.
    return { ok: true, creature: next, vitality, rootScalars: {}, rootFields, addIds,
      catalog: Object.keys(additions).length ? layerReadView(catalog ?? {}, additions) : catalog };
  } catch { return null; }
}
